/**
 * Create Booking Mutation Hook — envelope-returning (internalapi-envelope).
 *
 * `mutationFn` returns the `ApiResult` UNCHANGED: a business failure (declined
 * card, `promotion_rejected`, a bay taken in the meantime) is normal data on
 * the success path, not an exception — `apiClient` never throws, so TanStack's
 * error path is reserved for genuine programmer errors. Query invalidation is
 * gated on `result.success`.
 *
 * Converted with `useCreateUserPass` as the template, which the envelope
 * rollout names for mutations. The throw it replaced flattened
 * `{ code, details }` into one string, and `promotion_rejected` is precisely a
 * failure the caller has to branch on rather than render.
 *
 * @module booking/hooks
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '~/lib/query/keys';
import { bookingService } from '../api/bookingService';
import type { ApiErrorResult } from '~/lib/apiClient/types';
import type { CreateBookingPayload, ApiCreateBookingResponse } from '../api/types';

export interface UseCreateBookingOptions {
  /** The API answered success — receives the bare payload, as before. */
  onSuccess?: (result: ApiCreateBookingResponse) => void;

  /**
   * The API answered `success: false` — the full failure envelope
   * (`message`, `error.code`, `error.details`, `httpStatus`). This is the
   * expected-failure path; read `getPromotionRejection(result)` here for the
   * promo auto-drop.
   */
  onFailure?: (result: ApiErrorResult, payload: CreateBookingPayload) => void;

  /** Genuine exceptions only (programmer error) — apiClient never throws. */
  onError?: (error: Error) => void;
}

/**
 * Create booking mutation hook
 * 
 * @example
 * ```tsx
 * const createBooking = useCreateBooking({
 *   onSuccess: (booking) => {
 *     navigate(`/booking/confirmation/${booking.id}`);
 *   },
 * });
 * 
 * createBooking.mutate({
 *   clubId: 1,
 *   bayId: 2,
 *   userId: 123,
 *   startTime: '2025-12-16T10:00:00.000Z',
 *   endTime: '2025-12-16T11:00:00.000Z',
 *   paymentMethodId: 'cnon:xxx',
 *   paymentAmount: 50.00
 * });
 * ```
 */
export function useCreateBooking(options?: UseCreateBookingOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    // Never retry: creating a booking charges a card. A retried POST has no
    // cross-request idempotency behind it, so a transient failure after a
    // successful charge would double-book and double-charge.
    retry: false,

    mutationFn: (payload: CreateBookingPayload) => bookingService.createBooking(payload),

    onSuccess: (envelope, payload) => {
      if (!envelope.success) {
        options?.onFailure?.(envelope, payload);
        return;
      }
      const result = envelope.payload;
      const { booking } = result;
      // Invalidate bookings queries using centralized query keys
      // Invalidate all bookings for this club to ensure fresh data
      queryClient.invalidateQueries({
        queryKey: queryKeys.bookings.all(booking.clubId)
      });

      // Explicitly invalidate the non-club-scoped detail key.
      // bookings.detail() is not prefixed by clubId, so the all(clubId) invalidation
      // above does NOT cascade to it. Without this, a warm detail cache entry (e.g.
      // from a previous page visit) would not be refreshed after the new booking is created.
      queryClient.invalidateQueries({
        queryKey: queryKeys.bookings.detail(booking.id),
      });
      
      // Invalidate bay availability for the booked bay and date
      // Extract date from startTime (ISO format: "2025-12-16T10:00:00.000Z")
      const bookingDate = payload.startTime.split('T')[0];
      
      // Use availabilityForDate to match ALL queries for this bay/date regardless of time
      // This matches queries with ANY startTime/endTime values:
      // - ['clubId', 1, 'bays', 'availability', 2, '2025-12-16', '10:00', '11:00']
      // - ['clubId', 1, 'bays', 'availability', 2, '2025-12-16', '14:00', '15:00']
      // - ['clubId', 1, 'bays', 'availability', 2, '2025-12-16', undefined, undefined]
      queryClient.invalidateQueries({
        queryKey: queryKeys.bays.availabilityForDate(booking.clubId, payload.bayId, bookingDate),
      });

      options?.onSuccess?.(result);
    },
    
    onError: (error: Error) => {
      console.error('[useCreateBooking] Error:', error);
      options?.onError?.(error);
    },
  });
}
